import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import {
    FriendsApi,
    Token,
    IFriendEntryResponse,
    GetMyFriendsResponse,
    GetPendingFriendRequestsResponse,
    RespondFriendRequestBodyActionEnum,
} from "@devour/client";
import { IStore } from "@/redux/defaultStore";
import getConfig from "@/utils/getConfig";

/**
 * Query keys for cache management
 */
export const FRIENDS_QUERY_KEY = "friends";
export const FRIEND_REQUESTS_QUERY_KEY = "friend-requests";

/**
 * Incoming friend request with user info
 * Note: Matches backend IncomingFriendRequest schema
 */
export interface IncomingFriendRequest {
    odId: string;
    nickname?: string;
    discordUsername?: string;
    avatarUrl?: string;
    requestedAt?: number;
}

/**
 * Fetch friends list
 */
async function fetchFriends(fullToken: Token): Promise<GetMyFriendsResponse> {
    return new FriendsApi(getConfig(fullToken)).getMeFriends();
}

/**
 * Fetch pending friend requests
 */
async function fetchPendingRequests(fullToken: Token): Promise<GetPendingFriendRequestsResponse> {
    return new FriendsApi(getConfig(fullToken)).getMeFriendsPending();
}

interface UseFriendsOptions {
    enabled?: boolean;
}

interface UseFriendsReturn {
    // Data
    friends: IFriendEntryResponse[];
    onlineFriends: IFriendEntryResponse[];
    offlineFriends: IFriendEntryResponse[];
    /** Incoming friend requests with user info */
    incomingRequests: IncomingFriendRequest[];
    /** Outgoing friend requests (full data) */
    outgoingRequests: IFriendEntryResponse[];
    pendingCount: number;
    blockedCount: number;
    onlineCount: number;
    playingCount: number;

    // Loading states
    isLoading: boolean;
    isLoadingRequests: boolean;
    error: Error | null;

    // Actions
    sendRequest: (userId: string) => Promise<void>;
    acceptRequest: (userId: string) => Promise<void>;
    declineRequest: (userId: string) => Promise<void>;
    removeFriend: (friendId: string) => Promise<void>;
    blockUser: (userId: string) => Promise<void>;
    unblockUser: (userId: string) => Promise<void>;
    setLabel: (friendId: string, label: string | null) => Promise<void>;

    // Refresh
    refetch: () => void;
    refetchRequests: () => void;
}

/**
 * Hook for managing friends and friend requests
 *
 * Features:
 * - Friends list with online/offline status (future)
 * - Incoming and outgoing friend requests
 * - Friend actions: add, accept, decline, remove, block
 * - Nickname support
 *
 * Usage:
 *   const {
 *     friends,
 *     incomingRequests,
 *     sendRequest,
 *     acceptRequest,
 *   } = useFriends();
 */
export function useFriends(options: UseFriendsOptions = {}): UseFriendsReturn {
    const { enabled = true } = options;

    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const queryClient = useQueryClient();

    // Friends list query
    const {
        data: friendsData,
        isLoading,
        error,
        refetch,
    } = useQuery({
        queryKey: [FRIENDS_QUERY_KEY, fullToken?.id],
        queryFn: () => fetchFriends(fullToken!),
        enabled: Boolean(fullToken) && enabled,
        staleTime: 60 * 1000, // 1 minute - reduced for better responsiveness
        refetchOnWindowFocus: true, // Refresh when user returns to tab
    });

    // Pending requests query
    const {
        data: requestsData,
        isLoading: isLoadingRequests,
        refetch: refetchRequests,
    } = useQuery({
        queryKey: [FRIEND_REQUESTS_QUERY_KEY, fullToken?.id],
        queryFn: () => fetchPendingRequests(fullToken!),
        enabled: Boolean(fullToken) && enabled,
        staleTime: 2 * 60 * 1000, // 2 minutes - check more often for new requests
        refetchOnWindowFocus: true,
    });

    // Send friend request
    const sendRequestMutation = useMutation({
        mutationFn: async (targetUserId: string) => {
            await new FriendsApi(getConfig(fullToken!)).createFriendsRequest({
                sendFriendRequestBody: { targetUserId },
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [FRIEND_REQUESTS_QUERY_KEY] });
        },
    });

    // Accept friend request
    const acceptRequestMutation = useMutation({
        mutationFn: async (requesterId: string) => {
            await new FriendsApi(getConfig(fullToken!)).createFriendsRespond({
                respondFriendRequestBody: {
                    requesterId,
                    action: RespondFriendRequestBodyActionEnum.accept,
                },
            });
        },
        onMutate: async (requesterId: string) => {
            // Optimistic: remove from incoming requests
            await queryClient.cancelQueries({ queryKey: [FRIEND_REQUESTS_QUERY_KEY] });

            const previousRequests = queryClient.getQueryData<GetPendingFriendRequestsResponse>([
                FRIEND_REQUESTS_QUERY_KEY,
                fullToken?.id,
            ]);

            if (previousRequests) {
                queryClient.setQueryData(
                    [FRIEND_REQUESTS_QUERY_KEY, fullToken?.id],
                    {
                        ...previousRequests,
                        // Filter by odId since incoming is IncomingFriendRequest[] now
                        incoming: (previousRequests.incoming as unknown as IncomingFriendRequest[]).filter((req) => req.odId !== requesterId),
                    },
                );
            }

            return { previousRequests };
        },
        onSuccess: () => {
            // Refetch friends list to include new friend
            queryClient.invalidateQueries({ queryKey: [FRIENDS_QUERY_KEY] });
        },
        onError: (_err, _requesterId, context) => {
            if (context?.previousRequests) {
                queryClient.setQueryData(
                    [FRIEND_REQUESTS_QUERY_KEY, fullToken?.id],
                    context.previousRequests,
                );
            }
        },
    });

    // Decline friend request
    const declineRequestMutation = useMutation({
        mutationFn: async (requesterId: string) => {
            await new FriendsApi(getConfig(fullToken!)).createFriendsRespond({
                respondFriendRequestBody: {
                    requesterId,
                    action: RespondFriendRequestBodyActionEnum.decline,
                },
            });
        },
        onMutate: async (requesterId: string) => {
            await queryClient.cancelQueries({ queryKey: [FRIEND_REQUESTS_QUERY_KEY] });

            const previousRequests = queryClient.getQueryData<GetPendingFriendRequestsResponse>([
                FRIEND_REQUESTS_QUERY_KEY,
                fullToken?.id,
            ]);

            if (previousRequests) {
                queryClient.setQueryData(
                    [FRIEND_REQUESTS_QUERY_KEY, fullToken?.id],
                    {
                        ...previousRequests,
                        // Filter by odId since incoming is IncomingFriendRequest[] now
                        incoming: (previousRequests.incoming as unknown as IncomingFriendRequest[]).filter((req) => req.odId !== requesterId),
                    },
                );
            }

            return { previousRequests };
        },
        onError: (_err, _requesterId, context) => {
            if (context?.previousRequests) {
                queryClient.setQueryData(
                    [FRIEND_REQUESTS_QUERY_KEY, fullToken?.id],
                    context.previousRequests,
                );
            }
        },
    });

    // Remove friend
    const removeFriendMutation = useMutation({
        mutationFn: async (friendId: string) => {
            await new FriendsApi(getConfig(fullToken!)).deleteFriendsFriendId({ friendId });
        },
        onMutate: async (friendId: string) => {
            await queryClient.cancelQueries({ queryKey: [FRIENDS_QUERY_KEY] });

            const previousFriends = queryClient.getQueryData<GetMyFriendsResponse>([
                FRIENDS_QUERY_KEY,
                fullToken?.id,
            ]);

            if (previousFriends?.friendsList?.friends) {
                queryClient.setQueryData(
                    [FRIENDS_QUERY_KEY, fullToken?.id],
                    {
                        ...previousFriends,
                        friendsList: {
                            ...previousFriends.friendsList,
                            friends: previousFriends.friendsList.friends.filter((f: IFriendEntryResponse) => f.odId !== friendId),
                        },
                    },
                );
            }

            return { previousFriends };
        },
        onError: (_err, _friendId, context) => {
            if (context?.previousFriends) {
                queryClient.setQueryData(
                    [FRIENDS_QUERY_KEY, fullToken?.id],
                    context.previousFriends,
                );
            }
        },
    });

    // Block user
    const blockUserMutation = useMutation({
        mutationFn: async (userId: string) => {
            await new FriendsApi(getConfig(fullToken!)).createFriendsBlock({
                blockUserBody: { userId },
            });
        },
        onSuccess: () => {
            // Refresh both friends and requests as blocking removes friendships
            queryClient.invalidateQueries({ queryKey: [FRIENDS_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: [FRIEND_REQUESTS_QUERY_KEY] });
        },
    });

    // Unblock user
    const unblockUserMutation = useMutation({
        mutationFn: async (userId: string) => {
            await new FriendsApi(getConfig(fullToken!)).deleteFriendsBlockUserId({ userId });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [FRIENDS_QUERY_KEY] });
        },
    });

    // Set label (custom label you assign to a friend)
    const setLabelMutation = useMutation({
        mutationFn: async ({ friendId, label }: { friendId: string; label: string | null }) => {
            await new FriendsApi(getConfig(fullToken!)).updateFriendsFriendIdLabel({
                friendId,
                updateFriendLabelBody: {label},
            });
        },
        onMutate: async ({ friendId, label }) => {
            await queryClient.cancelQueries({ queryKey: [FRIENDS_QUERY_KEY] });

            const previousFriends = queryClient.getQueryData<GetMyFriendsResponse>([
                FRIENDS_QUERY_KEY,
                fullToken?.id,
            ]);

            if (previousFriends?.friendsList?.friends) {
                queryClient.setQueryData(
                    [FRIENDS_QUERY_KEY, fullToken?.id],
                    {
                        ...previousFriends,
                        friendsList: {
                            ...previousFriends.friendsList,
                            friends: previousFriends.friendsList.friends.map((f: IFriendEntryResponse) =>
                                f.odId === friendId ? { ...f, label } : f),
                        },
                    },
                );
            }

            return { previousFriends };
        },
        onError: (_err, _vars, context) => {
            if (context?.previousFriends) {
                queryClient.setQueryData(
                    [FRIENDS_QUERY_KEY, fullToken?.id],
                    context.previousFriends,
                );
            }
        },
    });

    // Extract data with defaults
    const rawFriends = friendsData?.friendsList?.friends ?? [];
    // Cast to IncomingFriendRequest[] since API now returns full objects (after client regen, cast won't be needed)
    const incomingRequests = (requestsData?.incoming ?? []) as unknown as IncomingFriendRequest[];
    const outgoingRequests = requestsData?.outgoing ?? [];
    const pendingCount = friendsData?.friendsList?.pendingReceivedCount ?? incomingRequests.length;
    const blockedCount = friendsData?.friendsList?.blockedCount ?? 0;

    // Separate online and offline friends
    const onlineFriends = rawFriends.filter((f: IFriendEntryResponse) => f.isOnline);
    const offlineFriends = rawFriends.filter((f: IFriendEntryResponse) => !f.isOnline);

    // Count friends playing games
    const playingCount = rawFriends.filter((f: IFriendEntryResponse) => f.isOnline && f.currentGame).length;

    // Sort friends: online first (playing first within online), then offline alphabetically
    const sortedFriends = [...rawFriends].sort((a: IFriendEntryResponse, b: IFriendEntryResponse) => {
        // Online friends first
        if (a.isOnline && !b.isOnline) return -1;
        if (!a.isOnline && b.isOnline) return 1;

        // If both online, friends playing games first
        if (a.isOnline && b.isOnline) {
            if (a.currentGame && !b.currentGame) return -1;
            if (!a.currentGame && b.currentGame) return 1;
        }

        // Alphabetically by display name (label > nickname > discordUsername)
        const nameA = (a.label || a.nickname || a.discordUsername || "").toLowerCase();
        const nameB = (b.label || b.nickname || b.discordUsername || "").toLowerCase();
        return nameA.localeCompare(nameB);
    });

    return {
        // Data
        friends: sortedFriends,
        onlineFriends,
        offlineFriends,
        incomingRequests,
        outgoingRequests,
        pendingCount,
        blockedCount,
        onlineCount: onlineFriends.length,
        playingCount,

        // Loading states
        isLoading,
        isLoadingRequests,
        error: error as Error | null,

        // Actions
        sendRequest: sendRequestMutation.mutateAsync,
        acceptRequest: acceptRequestMutation.mutateAsync,
        declineRequest: declineRequestMutation.mutateAsync,
        removeFriend: removeFriendMutation.mutateAsync,
        blockUser: blockUserMutation.mutateAsync,
        unblockUser: unblockUserMutation.mutateAsync,
        setLabel: (friendId: string, label: string | null) =>
            setLabelMutation.mutateAsync({ friendId, label }),

        // Refresh
        refetch,
        refetchRequests,
    };
}

export default useFriends;
